import React from 'react';
import PropTypes from 'prop-types';
import Card from 'components/ui/card/Card';
import CardText from 'components/ui/card/CardText';
import Header from 'components/ui/general/Header';
import Loader from 'components/loading/Loader';
import Pagination from 'components/list/Pagination/Pagination';
import Title from 'components/ui/general/Title';
import compose from 'recompose/compose';
import styled from 'styled-components';
import translate from 'components/hoc/translate';
import utils from 'helpers/utils';
import { SORT_ASC, SORT_DESC } from 'store/actions/types';
import { connect } from 'react-redux';

const ListStylingWrapper = styled.div`
    position: relative;
`;

const LoaderStylingWrapper = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #FFF;
    opacity: .9;
    z-index: 999;
    pointer-events: none;
`;

const LoaderStylingBlock = styled.div`
    margin-top: 50px;
    display: flex;
    width: 150px;
    height: 150px;
    justify-content: center;
    align-items: center;
    opacity: 1;
`;

class ResourceList extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            initLoading: true,
        };
    }

    componentDidMount() {
        const {
            filterList,
        } = this.props;

        this.fetchData(filterList);
    }

    componentDidUpdate(prevProps) {
        const {
            filterList,
            loading,
        } = this.props;

        const {
            initLoading,
        } = this.state;

        if (loading) {
            return;
        }

        if (initLoading && !loading) {
            this.setState({
                initLoading: false,
            });

            return;
        }

        const diff = utils.diffObjects(prevProps.filterList, filterList);

        if (!utils.isEmpty(diff)) {
            this.fetchData(filterList);
        }
    }

    fetchData = (filters) => {
        const {
            action,
            dispatchAction,
        } = this.props;

        dispatchAction(action(filters));
    }

    setPage = (newPage) => {
        const { actionType, updateListParams, updateQuery } = this.props;

        if (!newPage) {
            return;
        }

        if (this.props.page === newPage) {
            return;
        }

        const params = {
            page: newPage,
        };

        if (utils.isFunction(updateQuery)) {
            updateQuery(params);
        }

        return updateListParams({
            type: `${ actionType }_LIST_PAGE`,
            payload: params,
        });
    }

    setSort = (label) => {
        if (!label) {
            return;
        }

        const {
            actionType,
            params: {
                order,
            },
            updateListParams,
            updateQuery,
        } = this.props;

        const newOrder = (order === SORT_DESC) ? SORT_ASC : SORT_DESC;

        const params = {
            field: label,
            order: newOrder,
        };

        if (utils.isFunction(updateQuery)) {
            updateQuery(params);
        }

        updateListParams({
            type: `${ actionType }_LIST_SORT`,
            payload: params,
        });
    }

    render() {
        const {
            children,
            data,
            hidePagination,
            ids,
            loading,
            location,
            offset,
            page,
            params,
            renderNull,
            resource,
            size,
            title,
            total,
            translate,
        } = this.props;

        const basePath = location.pathname;

        const {
            initLoading,
        } = this.state;

        if (initLoading || loading) {
            return (
                <div>
                    { title &&
                        <Header>
                            <Title title={ translate(title) }/>
                        </Header>
                    }

                    <ListStylingWrapper>
                        <Card>
                            <LoaderStylingWrapper>
                                <LoaderStylingBlock>
                                    <Loader />
                                </LoaderStylingBlock>
                            </LoaderStylingWrapper>
                        </Card>
                    </ListStylingWrapper>
                </div>
            );
        }

        if (total === 0 && renderNull) {
            return null;
        }

        if (total === 0) {
            return (
                <ListStylingWrapper>
                    <Card>
                        <CardText style={ { padding: '20px' } }>
                            { translate('aor.navigation.no_results') }
                        </CardText>
                    </Card>
                </ListStylingWrapper>
            );
        }

        return (
            <div>
                { title &&
                    <Header>
                        <Title title={ translate(title) }/>
                    </Header>
                }

                <ListStylingWrapper>
                    <Card>
                        {
                            React.cloneElement(children, {
                                basePath: basePath, // We need this for the datagrid
                                currentSort: {
                                    field: params.field,
                                    order: params.order,
                                },
                                data: data,
                                ids: ids,
                                loading: loading,
                                resource: resource,
                                setSort: this.setSort,
                                size: params.size,
                            })
                        }

                        {
                            !hidePagination && <Pagination
                                currentPage={ page }
                                offset={ offset }
                                setPage={ this.setPage }
                                size={ size }
                                total={ total }
                            />
                        }
                    </Card>
                </ListStylingWrapper>
            </div>
        );
    }
}

ResourceList.defaultProps = {
    hidePagination: false,
};

ResourceList.propTypes = {
    action: PropTypes.func.isRequired,
    actionType: PropTypes.string.isRequired,
    children: PropTypes.element.isRequired,
    data: PropTypes.object,
    dispatchAction: PropTypes.func.isRequired,
    filterList: PropTypes.object.isRequired,
    hidePagination: PropTypes.bool,
    ids: PropTypes.array,
    loading: PropTypes.bool.isRequired,
    location: PropTypes.object.isRequired,
    offset: PropTypes.number,
    page: PropTypes.number,
    params: PropTypes.object,
    renderNull: PropTypes.bool,
    resource: PropTypes.string.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    sort: PropTypes.shape({
        field: PropTypes.string.isRequired,
        order: PropTypes.string,
    }).isRequired,
    title: PropTypes.string,
    total: PropTypes.number,
    translate: PropTypes.func.isRequired,
    updateListParams: PropTypes.func.isRequired,
    updateQuery: PropTypes.func,
};

const selectFilterData = (state) => {
    return {
        page: state.page,
        size: state.size,
        field: state.field,
        order: state.order,
        filter: state.filter,
    };
};

const mapStateToProps = (state, props) => {
    const {
        resource,
    } = props;

    const stateResource = state[resource];

    // This resource is essential for the ResourceList to work.
    if (utils.isUndefined(stateResource)) {
        throw new Error(`ResourceList - ${ resource } cannot be found in the redux state.`);
    }

    return {
        data: stateResource.data,
        filterCategory: stateResource.filterCategory,
        ids: stateResource.ids,
        loading: stateResource.loading,
        offset: stateResource.offset,
        page: stateResource.page,
        filterList: selectFilterData(stateResource),
        params: {
            page: stateResource.page,
            field: stateResource.field,
            filter: stateResource.filter,
            order: stateResource.order,
            size: stateResource.size,
        },
        size: stateResource.size,
        sort: {
            field: stateResource.field,
            order: stateResource.order,
        },
        total: stateResource.total,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        dispatchAction: (action) => {
            dispatch(action);
        },
        updateListParams: (action) => {
            dispatch(action);
        },
    };
};

const enhance = compose(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    ),
    translate,
);

export default enhance(ResourceList);



// WEBPACK FOOTER //
// ./src/components/list/Container/ResourceList.js